var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import * as React from 'react';
import FocusTrap from 'react-focus-lock';
/**
 * WithFocusLock
 *
 * The `WithFocusLock` higher order component provides two things:
 *
 * - `FocusLock` (provided by
 *   [react-focus-lock](https://github.com/theKashey/react-focus-lock)) to keep the
 *   user focus in the current layer so that keyboard navigation will be restricted
 *   to the elements within the `FocusLock`
 * - the `onEscKeyUp` handler that listens for the ESC keyup event to enable
 *   closing the current layer by pressing the escape key
 *
 * Example
 *
 * ```javascript
 *   const Layer = LayerComponent;
 *   const LayerComponentWithFocusLock = withFocusLock(Layer);
 *
 *   <Layer active={true}>
 *     // some content ...
 *   <Layer/>
 *
 *   <LayerComponentWithFocusLock
 *     active={true}
 *     onEscKeyUp={() => doSomething()}
 *   >
 *     // some content ...
 *   <LayerComponentWithFocusLock/>
 * ```
 */
export function withFocusLock(WrappedComponent) {
    var _a;
    return _a = class WithFocusLock extends React.PureComponent {
            constructor() {
                super(...arguments);
                this.handleKeyUp = (event) => {
                    const { onEscKeyUp } = this.props;
                    if (event.key === 'Escape' && onEscKeyUp) {
                        event.stopPropagation();
                        onEscKeyUp(event);
                    }
                };
            }
            render() {
                const _a = this.props, { 
                // eslint-disable-next-line @typescript-eslint/no-unused-vars -- The `onEscKeyUp` should not be included in the `wrappedComponentProps`
                onEscKeyUp, disableReturnFocus } = _a, wrappedComponentProps = __rest(_a, ["onEscKeyUp", "disableReturnFocus"]);
                return (React.createElement(FocusTrap, { returnFocus: !disableReturnFocus, autoFocus: true },
                    React.createElement("div", { onKeyUp: this.handleKeyUp },
                        React.createElement(WrappedComponent, Object.assign({}, wrappedComponentProps)))));
            }
        },
        _a.displayName = `WithFocusLock(${WrappedComponent.displayName})`,
        _a;
}
